/**
 * IssueReportsAdmin.jsx — super_admin queue for in-app bug reports.
 *
 * Lives at Settings → Admin Ops → "Issue Reports". Lists every report
 * submitted via the floating reporter, with filters by status + severity,
 * inline status transitions, internal note field, and a "view details"
 * row that expands to show the full captured context (URL, console logs,
 * network errors, viewport, user-agent) — i.e. everything Joshua would
 * normally have to ask the reporter for.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { AlertTriangle, CheckCircle2, Loader2, RefreshCw, Trash2, ChevronRight, ChevronDown, FlaskConical, Send } from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "@/App";
import { CommentsThread } from "@/components/IssueReportsMine";
import { useIssueDeepLink, IssueDeepLinkStyles } from "@/components/useIssueDeepLink";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = [
  { value: "new",         label: "New",         tint: "#2563eb" },
  { value: "triaging",    label: "Triaging",    tint: "#a16207" },
  { value: "in_progress", label: "In progress", tint: "#7c3aed" },
  { value: "fixed",       label: "Fixed",       tint: "#16a34a" },
  { value: "wontfix",     label: "Won't fix",   tint: "#6b7280" },
];
const SEVERITY_TINT = {
  low: "#9ca3af",
  medium: "#2563eb",
  high: "#dc2626",
  blocker: "#7f1d1d",
};

function StatusPill({ status, onChange, disabled }) {
  const opt = STATUS_OPTIONS.find((o) => o.value === status) || STATUS_OPTIONS[0];
  return (
    <select
      data-testid={`issue-status-select`}
      value={status}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      style={{
        padding: "4px 8px", borderRadius: 999, fontSize: 11, fontWeight: 600,
        border: `0.5px solid ${opt.tint}`, color: opt.tint, background: "transparent",
        cursor: disabled ? "wait" : "pointer", fontFamily: "inherit",
      }}
    >
      {STATUS_OPTIONS.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

function SeverityChip({ severity }) {
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase",
      color: SEVERITY_TINT[severity] || "#6b7280",
      border: `0.5px solid ${SEVERITY_TINT[severity] || "#6b7280"}`,
      borderRadius: 4, padding: "2px 6px",
    }}>
      {severity}
    </span>
  );
}

const KIND_TINT = {
  bug:     { fg: "#dc2626", bg: "rgba(220,38,38,0.10)", label: "Bug" },
  feature: { fg: "#7c3aed", bg: "rgba(124,58,237,0.10)", label: "Improvement" },
  comment: { fg: "#2563eb", bg: "rgba(37,99,235,0.10)", label: "Comment" },
  other:   { fg: "#a16207", bg: "rgba(161,98,7,0.10)",  label: "Other" },
};
function KindChip({ kind }) {
  const k = KIND_TINT[kind] || KIND_TINT.bug;
  return (
    <span data-testid={`issue-kind-${kind || "bug"}`} style={{
      fontSize: 10, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase",
      color: k.fg, background: k.bg,
      borderRadius: 4, padding: "2px 6px",
    }}>
      {k.label}
    </span>
  );
}

// ── Iter 188.7 (Feb 2026) — Stage + TAT chips (admin view) ────────
// Same visual language as IssueReportsMine.jsx so admins see the
// exact chips reporters see. Driven by the same backend-computed
// fields on `/api/issue-reports` responses.
const STAGE_TINT_ADMIN = {
  "Open":         { fg: "#2563eb", bg: "rgba(37,99,235,0.10)" },
  "In progress":  { fg: "#7c3aed", bg: "rgba(124,58,237,0.10)" },
  "Closed":       { fg: "#6b7280", bg: "rgba(107,114,128,0.10)" },
};
function StageChipAdmin({ stage }) {
  const t = STAGE_TINT_ADMIN[stage] || STAGE_TINT_ADMIN.Open;
  return (
    <span
      data-testid={`issue-stage-${(stage || "open").toLowerCase().replace(/\s+/g, "-")}`}
      style={{
        fontSize: 10, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase",
        color: t.fg, background: t.bg,
        borderRadius: 4, padding: "2px 6px",
      }}
    >
      {stage || "Open"}
    </span>
  );
}
function _fmtDurationHoursAdmin(hoursRaw) {
  if (hoursRaw == null || !Number.isFinite(hoursRaw)) return "—";
  const overdue = hoursRaw < 0;
  const total = Math.abs(hoursRaw);
  const days = Math.floor(total / 24);
  const hours = Math.floor(total - days * 24);
  const minutes = Math.max(0, Math.round((total - Math.floor(total)) * 60));
  let compact;
  if (days > 0) compact = `${days}d ${hours}h`;
  else if (hours > 0) compact = `${hours}h ${minutes}m`;
  else compact = `${minutes}m`;
  return overdue ? `${compact} overdue` : `${compact} left`;
}
function TatChipAdmin({ report }) {
  const hr = report?.hours_remaining;
  const breached = !!report?.tat_breached;
  const frozen = !!report?.tat_frozen;
  const slaHours = report?.tat_hours;
  if (hr == null || slaHours == null) return null;
  let fg, bg;
  if (frozen && !breached) { fg = "#15803d"; bg = "rgba(21,128,61,0.08)"; }
  else if (breached) { fg = "#dc2626"; bg = "rgba(220,38,38,0.10)"; }
  else if (hr / slaHours < 0.2) { fg = "#b45309"; bg = "rgba(180,83,9,0.10)"; }
  else { fg = "#374151"; bg = "rgba(55,65,81,0.06)"; }
  const label = frozen
    ? (breached ? `Closed · ${_fmtDurationHoursAdmin(-Math.abs(hr))}` : `Closed · ${_fmtDurationHoursAdmin(hr)}`)
    : _fmtDurationHoursAdmin(hr);
  const tip = `SLA: ${slaHours}h from submission` +
    (report?.tat_deadline_utc ? ` · Due ${new Date(report.tat_deadline_utc).toLocaleString()}` : "");
  return (
    <span
      data-testid={`issue-tat-${report.ticket_id || report.id}`}
      title={tip}
      style={{
        fontSize: 11, fontWeight: 600,
        color: fg, background: bg,
        border: `0.5px solid ${fg}33`,
        padding: "2px 8px", borderRadius: 999,
      }}
    >
      {label}
    </span>
  );
}

export default function IssueReportsAdmin() {
  const { token } = useAuth();
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [severityFilter, setSeverityFilter] = useState("");
  const [kindFilter, setKindFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const { ticketId: deepLinkTicket, registerRow } = useIssueDeepLink();
  // One-shot guard: filter-clear + toast should only fire on the first
  // resolution of a given deep-link ticket_id.
  const deepLinkHandledRef = useRef(null);

  // Deep-link: if URL carries ?ticket=BUG-####, drop any restrictive
  // filters so the target row is guaranteed to be included in the list.
  useEffect(() => {
    if (!deepLinkTicket) return;
    if (deepLinkHandledRef.current === deepLinkTicket) return;
    deepLinkHandledRef.current = deepLinkTicket;
    if (statusFilter || severityFilter || kindFilter) {
      setStatusFilter("");
      setSeverityFilter("");
      setKindFilter("");
    }
  }, [deepLinkTicket, statusFilter, severityFilter, kindFilter]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (severityFilter) params.severity = severityFilter;
      if (kindFilter) params.kind = kindFilter;
      // On deep-link, widen the page window so older tickets are reachable
      // without a targeted lookup endpoint. Backend caps at 500.
      if (deepLinkTicket) params.limit = 500;
      const [r, s] = await Promise.all([
        axios.get(`${API}/api/issue-reports`, { headers, params }),
        axios.get(`${API}/api/issue-reports/stats`, { headers }),
      ]);
      setReports(r.data?.items || []);
      setStats(s.data || null);
    } catch (e) {
      toast.error("Couldn't load reports");
    } finally {
      setLoading(false);
    }
  }, [token, statusFilter, severityFilter, kindFilter, deepLinkTicket]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Auto-expand the deep-linked row + surface a "not found" toast if the
  // ticket isn't in the returned page (edge case: deleted or filtered-out
  // beyond the 500-row cap). Runs after each successful fetch.
  useEffect(() => {
    if (!deepLinkTicket || loading || reports.length === 0) return;
    const hit = reports.find((r) => r.ticket_id === deepLinkTicket);
    if (hit) {
      setExpanded((prev) => (prev[hit.id] ? prev : { ...prev, [hit.id]: true }));
    } else if (deepLinkHandledRef.current === deepLinkTicket) {
      // Fire once (guard via the same ref that filter-clear uses).
      toast.error(`Ticket ${deepLinkTicket} not found in the current list.`);
      deepLinkHandledRef.current = `${deepLinkTicket}::missed`;
    }
  }, [deepLinkTicket, reports, loading]);

  const setStatus = async (id, next) => {
    try {
      await axios.patch(`${API}/api/issue-reports/${id}`, { status: next }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Marked ${next}`);
      fetchAll();
    } catch { toast.error("Couldn't update"); }
  };

  const saveNote = async (id, note) => {
    try {
      await axios.patch(`${API}/api/issue-reports/${id}`, { admin_note: note }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Note saved");
    } catch { toast.error("Couldn't save note"); }
  };

  const saveResolutionMessage = async (id, msg) => {
    try {
      await axios.patch(`${API}/api/issue-reports/${id}`, { resolution_message: msg }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Resolution message sent to reporter");
      fetchAll();
    } catch { toast.error("Couldn't save resolution message"); }
  };

  const deleteReport = async (id) => {
    if (!window.confirm("Delete this report? This can't be undone.")) return;
    try {
      await axios.delete(`${API}/api/issue-reports/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Deleted");
      fetchAll();
    } catch { toast.error("Couldn't delete"); }
  };

  // Iter 338 — public comment from Support to the reporter. Fires a
  // bell notification server-side so the user knows there's an update.
  const postComment = async (id, text) => {
    const trimmed = (text || "").trim();
    if (!trimmed) return false;
    try {
      await axios.post(`${API}/api/issue-reports/${id}/comments`, { text: trimmed }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Comment sent to reporter");
      fetchAll();
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't post comment");
      return false;
    }
  };

  const summaryCards = useMemo(() => {
    if (!stats) return null;
    const cards = [
      { label: "Open", value: stats.open || 0, tint: "#2563eb" },
      { label: "Bugs", value: stats.by_kind?.bug || 0, tint: "#dc2626" },
      { label: "Suggestions", value: stats.by_kind?.feature || 0, tint: "#7c3aed" },
      { label: "Comments", value: stats.by_kind?.comment || 0, tint: "#2563eb" },
      { label: "Blockers (open)", value: stats.by_severity?.blocker || 0, tint: "#7f1d1d" },
      { label: "Fixed",      value: stats.by_status?.fixed || 0, tint: "#16a34a" },
    ];
    return (
      <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 10, marginBottom: 18 }}>
        {cards.map((c) => (
          <div key={c.label} style={{
            border: "0.5px solid var(--border)", borderRadius: 10, padding: "12px 14px",
          }}>
            <div style={{ fontSize: 10, color: "var(--text-tertiary)", textTransform: "uppercase",
              letterSpacing: "0.06em", fontWeight: 600 }}>{c.label}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: c.tint, marginTop: 4 }}>{c.value}</div>
          </div>
        ))}
      </div>
    );
  }, [stats]);

  return (
    <div data-testid="settings-issue-reports">
      <IssueDeepLinkStyles />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div>
          <h2 style={{ fontSize: 16, fontWeight: 650, margin: 0 }}>Issue reports</h2>
          <p style={{ fontSize: 12, color: "var(--text-tertiary)", margin: "4px 0 0 0" }}>
            Bug reports submitted by any team member via the floating "Report issue" button.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            type="button"
            data-testid="open-analyzer-btn"
            onClick={() => window.open("/issue-reports-analyzer", "_blank", "noopener")}
            style={{
              padding: "6px 12px", border: "0.5px solid #6366f1", borderRadius: 8,
              background: "white", fontSize: 12, fontWeight: 600, color: "#6366f1",
              cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6,
            }}
            title="Open the read-only analyzer (production data, new tab)"
          >
            <FlaskConical style={{ width: 12, height: 12 }} />
            Analyzer
          </button>
          <button
            type="button"
            onClick={fetchAll}
            style={{
              padding: "6px 12px", border: "0.5px solid var(--border)", borderRadius: 8,
              background: "white", fontSize: 12, fontWeight: 600, color: "var(--text-secondary)",
              cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6,
            }}
          >
            {loading ? <Loader2 className="animate-spin" style={{ width: 12, height: 12 }} /> : <RefreshCw style={{ width: 12, height: 12 }} />}
            Refresh
          </button>
        </div>
      </div>

      {summaryCards}

      <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
        <select
          data-testid="issue-kind-filter"
          value={kindFilter} onChange={(e) => setKindFilter(e.target.value)}
          style={{ padding: "6px 10px", border: "0.5px solid var(--border)", borderRadius: 6, fontSize: 12 }}
        >
          <option value="">All kinds</option>
          <option value="bug">Bugs</option>
          <option value="feature">Improvements / Suggestions</option>
          <option value="comment">Comments</option>
          <option value="other">Other / Forwarded</option>
        </select>
        <select
          value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          style={{ padding: "6px 10px", border: "0.5px solid var(--border)", borderRadius: 6, fontSize: 12 }}
        >
          <option value="">All statuses</option>
          {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select
          value={severityFilter} onChange={(e) => setSeverityFilter(e.target.value)}
          style={{ padding: "6px 10px", border: "0.5px solid var(--border)", borderRadius: 6, fontSize: 12 }}
        >
          <option value="">All severities</option>
          {["low","medium","high","blocker"].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {reports.length === 0 ? (
        <div style={{
          border: "0.5px dashed var(--border)", borderRadius: 10, padding: "32px 16px",
          textAlign: "center", color: "var(--text-tertiary)", fontSize: 13,
        }}>
          {loading ? "Loading…" : "No reports yet — when someone clicks the bug icon, you'll see their report here."}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {reports.map((r) => {
            const isOpen = !!expanded[r.id];
            return (
              <div
                key={r.id}
                data-testid={`issue-report-row-${r.id}`}
                ref={(node) => r.ticket_id && registerRow(r.ticket_id, node)}
                style={{
                border: "0.5px solid var(--border)", borderRadius: 10, overflow: "hidden",
              }}>
                <div style={{ padding: "12px 14px", display: "flex", alignItems: "flex-start", gap: 12 }}>
                  <button
                    type="button"
                    onClick={() => setExpanded((e) => ({ ...e, [r.id]: !e[r.id] }))}
                    style={{ background: "none", border: 0, cursor: "pointer", padding: 0, color: "var(--text-tertiary)", marginTop: 2 }}
                    aria-label={isOpen ? "Collapse" : "Expand"}
                  >
                    {isOpen ? <ChevronDown style={{ width: 14, height: 14 }} /> : <ChevronRight style={{ width: 14, height: 14 }} />}
                  </button>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
                      {r.ticket_id && (
                        <span
                          data-testid={`issue-ticket-id-${r.ticket_id}`}
                          style={{
                            fontFamily: "ui-monospace, monospace", fontSize: 11, fontWeight: 700,
                            color: "var(--text-secondary)", background: "var(--bg-subtle, #f3f4f6)",
                            padding: "2px 6px", borderRadius: 4,
                          }}
                        >
                          {r.ticket_id}
                        </span>
                      )}
                      <KindChip kind={r.kind || "bug"} />
                      {(r.kind || "bug") === "bug" && <SeverityChip severity={r.severity} />}
                      <StageChipAdmin stage={r.stage} />
                      <StatusPill status={r.status} onChange={(v) => setStatus(r.id, v)} />
                      <TatChipAdmin report={r} />
                      <span style={{ fontSize: 11, color: "var(--text-tertiary)" }}>
                        {r.reporter_name || r.reporter_email} · {r.reporter_role || "—"}
                      </span>
                      <span
                        data-testid={`issue-escalated-date-${r.ticket_id || r.id}`}
                        style={{ fontSize: 11, color: "var(--text-tertiary)" }}
                        title="Escalated / submitted date"
                      >
                        · {new Date(r.created_at).toLocaleString()}
                      </span>
                    </div>
                    <div style={{ fontSize: 13, color: "var(--text-primary)", whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                      {r.description}
                    </div>
                    {r.extra && (r.extra.forwarded_from_email || r.extra.subject_area) && (
                      <div data-testid={`issue-forwarded-${r.id}`} style={{ fontSize: 11, color: "var(--text-secondary)", marginTop: 6, padding: "6px 8px", background: "rgba(161,98,7,0.06)", border: "0.5px solid rgba(161,98,7,0.20)", borderRadius: 6, display: "flex", gap: 12, flexWrap: "wrap" }}>
                        {r.extra.forwarded_from_email && (
                          <span><strong>Forwarded from:</strong> {r.extra.forwarded_from_email}</span>
                        )}
                        {r.extra.subject_area && (
                          <span><strong>Page / area:</strong> {r.extra.subject_area}</span>
                        )}
                      </div>
                    )}
                    <div style={{ fontSize: 11, color: "var(--text-tertiary)", marginTop: 6 }}>
                      <span style={{ fontFamily: "ui-monospace, monospace" }}>{r.route || r.url}</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => deleteReport(r.id)}
                    style={{ background: "none", border: 0, cursor: "pointer", color: "var(--text-tertiary)", padding: 4 }}
                    title="Delete report"
                    data-testid={`issue-delete-${r.id}`}
                  >
                    <Trash2 style={{ width: 13, height: 13 }} />
                  </button>
                </div>

                {isOpen && (
                  <div style={{ borderTop: "0.5px solid var(--border)", background: "var(--bg-subtle, #f9fafb)", padding: "14px 18px 16px 36px" }}>
                    <ExpandedDetail
                      report={r}
                      onSaveNote={(note) => saveNote(r.id, note)}
                      onSaveResolutionMessage={(msg) => saveResolutionMessage(r.id, msg)}
                      onPostComment={(text) => postComment(r.id, text)}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ExpandedDetail({ report, onSaveNote, onSaveResolutionMessage, onPostComment }) {
  const [note, setNote] = useState(report.admin_note || "");
  const [resMsg, setResMsg] = useState(report.resolution_message || "");
  const [newComment, setNewComment] = useState("");
  const [posting, setPosting] = useState(false);
  const isResolved = report.status === "fixed" || report.status === "wontfix";

  const handlePostComment = async () => {
    if (!newComment.trim() || posting) return;
    setPosting(true);
    const ok = await onPostComment(newComment);
    setPosting(false);
    if (ok) setNewComment("");
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* Iter 338 — Support comments thread (visible to reporter via /mine
          and to admins here). Post box is admin-only for now (backend
          enforces _require_admin on POST /issue-reports/{id}/comments). */}
      <div data-testid={`issue-comments-block-${report.id}`}>
        <CommentsThread comments={report.support_comments || []} readOnly={false} />
        <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 6 }}>
          <textarea
            data-testid={`issue-comment-input-${report.id}`}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            rows={2}
            placeholder="Reply to reporter (fix details, request for info, or reason for won't-fix)…"
            style={{
              width: "100%", padding: "8px 10px", border: "0.5px solid var(--border)",
              borderRadius: 6, fontSize: 12, fontFamily: "inherit", resize: "vertical",
              background: "white",
            }}
          />
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
            <span style={{ fontSize: 10, color: "var(--text-tertiary)" }}>
              Reporter will get a bell notification the moment you post.
            </span>
            <button
              type="button"
              data-testid={`issue-comment-post-${report.id}`}
              onClick={handlePostComment}
              disabled={posting || !newComment.trim()}
              style={{
                padding: "6px 12px", border: 0,
                background: newComment.trim() && !posting ? "var(--navy, #0f172a)" : "#9ca3af",
                color: "white", borderRadius: 6, fontSize: 11, fontWeight: 600,
                cursor: posting || !newComment.trim() ? "not-allowed" : "pointer",
                display: "inline-flex", alignItems: "center", gap: 6,
              }}
            >
              {posting
                ? <Loader2 className="animate-spin" style={{ width: 12, height: 12 }} />
                : <Send style={{ width: 12, height: 12 }} />}
              {posting ? "Posting…" : "Post comment"}
            </button>
          </div>
        </div>
      </div>
      <DetailRow label="URL"><code style={{ fontSize: 11 }}>{report.url}</code></DetailRow>
      <DetailRow label="User-agent"><code style={{ fontSize: 11 }}>{report.user_agent}</code></DetailRow>
      <DetailRow label="Viewport">
        <code style={{ fontSize: 11 }}>
          {report.viewport?.w}×{report.viewport?.h} (dpr {report.viewport?.dpr || 1})
        </code>
      </DetailRow>

      {report.network_errors?.length > 0 && (
        <DetailRow label={`Network errors (${report.network_errors.length})`}>
          <div style={{ background: "white", border: "0.5px solid var(--border)", borderRadius: 6, padding: 8, maxHeight: 160, overflow: "auto", fontFamily: "ui-monospace, monospace", fontSize: 11 }}>
            {report.network_errors.slice(-20).map((n, i) => (
              <div key={i} style={{ color: n.status >= 500 ? "#dc2626" : "#a16207" }}>
                {n.ts?.slice(11, 19)} · {n.status || "ERR"} · {n.url}
              </div>
            ))}
          </div>
        </DetailRow>
      )}

      {report.console_logs?.length > 0 && (
        <DetailRow label={`Console (last ${report.console_logs.length})`}>
          <div style={{ background: "white", border: "0.5px solid var(--border)", borderRadius: 6, padding: 8, maxHeight: 220, overflow: "auto", fontFamily: "ui-monospace, monospace", fontSize: 11 }}>
            {report.console_logs.slice(-50).map((l, i) => (
              <div key={i} style={{
                color: l.level === "error" ? "#dc2626" : l.level === "warn" ? "#a16207" : "var(--text-secondary)",
              }}>
                [{l.level}] {l.ts?.slice(11, 19)} · {l.msg}
              </div>
            ))}
          </div>
        </DetailRow>
      )}

      {/* Iter 177.4 (Lisa #76669993 Feb 2026) — render attachments
          inline. Images get a thumbnail (data URI), everything else
          gets a 'Download' link. The data is stored inline on the
          issue_reports doc as base64, so we never touch object
          storage and can fetch it back without a second round-trip. */}
      {Array.isArray(report.attachments) && report.attachments.length > 0 && (
        <DetailRow label={`Attachments (${report.attachments.length})`}>
          <div data-testid="issue-detail-attachments" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {report.attachments.map((a, i) => {
              const dataUri = `data:${a.content_type || "application/octet-stream"};base64,${a.data_b64}`;
              const isImage = (a.content_type || "").startsWith("image/");
              return (
                <div
                  key={`${a.name}-${i}`}
                  data-testid={`issue-detail-attachment-${i}`}
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: 8, background: "white",
                    border: "0.5px solid var(--border)", borderRadius: 6,
                  }}
                >
                  {isImage ? (
                    <a href={dataUri} target="_blank" rel="noreferrer" style={{ flexShrink: 0 }}>
                      <img
                        src={dataUri}
                        alt={a.name}
                        style={{
                          maxWidth: 96, maxHeight: 64, objectFit: "cover",
                          border: "0.5px solid var(--border)", borderRadius: 4,
                          cursor: "zoom-in",
                        }}
                      />
                    </a>
                  ) : (
                    <div style={{ width: 36, height: 36, background: "var(--page-bg, #f3f4f6)", borderRadius: 4, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, color: "var(--text-tertiary)", flexShrink: 0 }}>
                      📄
                    </div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-primary)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {a.name}
                    </div>
                    <div style={{ fontSize: 10, color: "var(--text-tertiary)", marginTop: 1 }}>
                      {a.content_type || "binary"} · {a.size ? `${(a.size / 1024).toFixed(0)} KB` : ""}
                    </div>
                  </div>
                  <a
                    href={dataUri}
                    download={a.name}
                    data-testid={`issue-detail-attachment-download-${i}`}
                    style={{
                      fontSize: 11, fontWeight: 600, color: "var(--blue, #2563eb)",
                      padding: "4px 10px", border: "0.5px solid var(--border)",
                      borderRadius: 6, textDecoration: "none",
                    }}
                  >
                    Download
                  </a>
                </div>
              );
            })}
          </div>
        </DetailRow>
      )}

      <DetailRow label="Internal note (super_admin only)">
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={2}
          style={{
            width: "100%", padding: "8px 10px", border: "0.5px solid var(--border)",
            borderRadius: 6, fontSize: 12, fontFamily: "inherit", resize: "vertical",
          }}
          placeholder="What did you find? What's the fix?"
        />
        <div style={{ marginTop: 6, display: "flex", justifyContent: "flex-end" }}>
          <button
            type="button"
            onClick={() => onSaveNote(note)}
            style={{
              padding: "6px 12px", border: 0, background: "var(--navy)",
              color: "white", borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: "pointer",
            }}
          >
            Save note
          </button>
        </div>
      </DetailRow>

      <DetailRow label="Resolution message (sent to reporter as a notification)">
        <textarea
          data-testid={`issue-resolution-message-${report.id}`}
          value={resMsg}
          onChange={(e) => setResMsg(e.target.value)}
          rows={3}
          style={{
            width: "100%", padding: "8px 10px", border: "0.5px solid var(--border)",
            borderRadius: 6, fontSize: 12, fontFamily: "inherit", resize: "vertical",
          }}
          placeholder="What was changed? Any next steps for the reporter? They'll receive this as an in-app notification when you mark Fixed or Won't fix."
        />
        <div style={{ marginTop: 6, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 10, color: "var(--text-tertiary)" }}>
            {isResolved
              ? "Saving again will re-notify the reporter with the updated message."
              : "Will be sent the moment you mark this Fixed or Won't fix."}
          </span>
          <button
            type="button"
            data-testid={`issue-resolution-save-${report.id}`}
            onClick={() => onSaveResolutionMessage(resMsg)}
            style={{
              padding: "6px 12px", border: 0, background: "var(--navy, #0f172a)",
              color: "white", borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: "pointer",
            }}
          >
            Save resolution message
          </button>
        </div>
      </DetailRow>

      {report.resolved_at && (
        <div style={{ fontSize: 11, color: "var(--success, #16a34a)", display: "inline-flex", alignItems: "center", gap: 6 }}>
          <CheckCircle2 style={{ width: 12, height: 12 }} />
          Resolved by {report.resolved_by || "—"} at {new Date(report.resolved_at).toLocaleString()}
        </div>
      )}
    </div>
  );
}

function DetailRow({ label, children }) {
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text-tertiary)", marginBottom: 6 }}>{label}</div>
      {children}
    </div>
  );
}
